
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDashboardSummary } from '../../actions/dashboard';
import { useTheme, useMediaQuery } from '@mui/material';


import {
  DashboardContainer,
  FirstRow,
  GridOfSix,
  StockColumn,
  SecondRow,
  MainSquare,
  SideGrid,
  SideWidget,
} from './styles';

import StatsWidget from './Widgets/StatsWidget';
import BatchesWidget from './Widgets/TotalBatches';
import MedicineTabs from './Widgets/InventoryWidget';
import InventoryIcon from '@mui/icons-material/Inventory2';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventIcon from '@mui/icons-material/Event';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import AlarmIcon from '@mui/icons-material/Alarm';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Typography } from '@mui/material';
import SearchPromoWidget from './Widgets/SearchWidget';
import StockWidget from './Widgets/StockWidget';

const Dashboard = () => {
  const dispatch = useDispatch();
  const summary = useSelector((state) => state?.dashboard?.summary);
  const s = summary || {};
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));


  // Fetch on mount + when the tab becomes visible again
  useEffect(() => {
    dispatch(getDashboardSummary());
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        dispatch(getDashboardSummary());
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [dispatch]);
  

  const tiles = [
    {
      bgColor: '#c1cd32',
      iconBg: '#bbf7d0',
      icon: InventoryIcon,
      label: isMobile ? 'Exp in 6m' : 'Expiring in 6 months',
      quantity: String(s.expiring180 ?? '—'),
      qtyColor: '#16a34a',
      ctaHref: '/inventory?filter=180days',
      ctaText: 'View',
    },
    {
      bgColor: '#ffda00',
      iconBg: '#fef9c3',
      icon: CalendarMonthIcon,
      label: isMobile ? 'Exp in 3m' : 'Expiring in 3 Months',
      quantity: String(s.expiring90 ?? '—'),
      qtyColor: '#ca8a04',
      ctaHref: '/inventory?filter=90days',
      ctaText: 'View',
    },
    {
      bgColor: '#ffba00',
      iconBg: '#ffedd5',
      icon: EventIcon,
      label: isMobile ? 'Exp in 1m' : 'Expiring in 1 Month',
      quantity: String(s.expiring30 ?? '—'),
      qtyColor: '#d97706',
      ctaHref: '/inventory?filter=30days',
      ctaText: 'View',
    },
    {
      bgColor: '#ff8904',
      iconBg: '#fef3c7',
      icon: HourglassBottomIcon,
      label: isMobile ? 'Exp in 1w' : 'Expiring in 1 Week',
      quantity: String(s.expiring7 ?? '—'),
      qtyColor: '#ea580c',
      ctaHref: '/inventory?filter=7days',
      ctaText: 'View',
    },
    {
      bgColor: '#ff3400c2',
      iconBg: '#fee2e2',
      icon: AlarmIcon,
      label: isMobile ? 'Exp in 1d' : 'Expiring in 1 day',
      quantity: String(s.expiring1 ?? '—'),
      qtyColor: '#f97316',
      ctaHref: '/inventory?filter=1day',
      ctaText: 'View',
    },
    {
      bgColor: '#ff0000c2',
      iconBg: '#fecaca',
      icon: WarningAmberIcon,
      label: 'Expired',
      quantity: String(s.expired ?? '—'),
      qtyColor: '#dc2626',
      ctaHref: '/inventory?filter=expired',
      ctaText: 'View',
    },
  ];

  const ttiles = [
  {
    bgColor: 'rgba(255, 200, 0, 0.2)',
    color: '#ffc800',
    icon: RemoveCircleIcon,
    label: 'Low Stock',
    quantity: String(s.lowStock ?? '—'),
    ctaHref: '/inventory?stock=low',
    ctaText: 'View',
  },
  {
    bgColor: 'rgba(255, 92, 0, 0.125)',
    color: '#ff5c00',
    icon: WarningIcon,
    label: 'Critically Low',
    quantity: String(s.criticalStock ?? '—'),
    ctaHref: '/inventory?stock=critical',
    ctaText: 'View',
  },
  {
    bgColor: 'rgba(239, 83, 80, 0.1)',
    color: '#EF5350',
    icon: ErrorIcon,
    label: 'Out of Stock',
    quantity: String(s.outOfStock ?? '—'),
    ctaHref: '/inventory?stock=out',
    ctaText: 'View',
  },
];



  return (
    <DashboardContainer>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '90%', margin: 'auto', py: '15px', pb: '32px' }}>
        <Typography variant="h3" sx={{ 
      fontSize: {
      xs: '1.6rem',  // mobile
      sm: '2rem',    // small tablets
      md: '2.5rem',  // desktop
    }, 
    fontWeight: 600 }}>Dashboard</Typography>
      </Box>

      <Box sx={{ width: {xs:'95%', md:'90%'}, marginLeft:{xs:'5%'}, margin: 'auto' }}>
        <FirstRow sx={{}}>
          <StatsWidget
            totalBatches={s.totalBatches}
            lastBatchAddedAt={s.lastBatchAddedAt}
            activeAlerts={s.activeAlerts}
            newAlertsToday={s.newAlertsToday}
          />

          <GridOfSix>
            {tiles.map((t, i) => (
              <BatchesWidget key={i} {...t} />
            ))}
          </GridOfSix>
          <StockColumn>
            {ttiles.map((t, i) => (
              <StockWidget key={i} {...t} />
            ))}
            
          </StockColumn>
        </FirstRow>

        <SecondRow>
          <MainSquare>
            <MedicineTabs
  expiringSoon={summary?.expiringSoon ?? []}
  recentlyAdded={summary?.recentlyAdded ?? []}
/>
          </MainSquare>

          <SideGrid>
            <SideWidget>
              <SearchPromoWidget />
            </SideWidget>
          </SideGrid>
        </SecondRow>
      </Box>
    </DashboardContainer>
  );
};

export default Dashboard;
